import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Icon, Message, Modal, Table } from '@weezevent/nacre';

import { TranslatableInput } from '../../components';

import css from '../actions.module.css';

export const ActionComments = ({ action, handleAction, handleSave, languageOptions, defaultLanguage }) => {
    const { t } = useTranslation();
    const [modalComment, setModalComment] = React.useState(null);

    const handleDelete = React.useCallback(
        comment => {
            handleAction(
                'comment_choices',
                action.comment_choices.filter(cmt => cmt.id !== comment.id)
            );
        },
        [action.comment_choices]
    );

    const handleEdit = React.useCallback(comment => {
        if (!comment) {
            return setModalComment({ translations: { label: {}, comment: {} } });
        }
        setModalComment(comment);
    }, []);

    const handleClose = React.useCallback(() => {
        if (!modalComment.id) {
            if (!modalComment.label || !modalComment.comment) {
                // Remove invalid comments on closing
                handleAction('comment_choices', action.comment_choices.slice(0, action.comment_choices.length));
            }
        }

        setModalComment(null);
    }, [action.comment_choices, modalComment]);

    return (
        <>
            <Message type="default" title={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_label')}>
                {t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_message')}
            </Message>

            {!!action.comment_choices.length && (
                <>
                    <Table useNew>
                        <Table.Header>
                            <Table.Tr>
                                <th>{t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_table.label')}</th>
                                <th>{t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_table.actions')}</th>
                            </Table.Tr>
                        </Table.Header>
                        <Table.TBody>
                            {action.comment_choices.map((comment, index) => (
                                <Table.Tr key={index}>
                                    <Table.Td className={css['width-x-large']}>{comment.label}</Table.Td>
                                    <Table.Td lassName={css['width-x-mini']}>
                                        <Icon name="cog" onClick={() => handleEdit(comment)} />
                                        <Icon name="trash" onClick={() => handleDelete(comment)} />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.TBody>
                    </Table>

                    <Button
                        className={css['comments_table_cta']}
                        color="ticket"
                        inverted
                        label={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_empty_state.cta')}
                        icon={<Icon name="plus" onClick={null} size="regular" />}
                        onClick={() => handleEdit()}
                    />
                </>
            )}
            {Boolean(modalComment) && (
                <CommentModal
                    action={action}
                    comment={modalComment}
                    defaultLanguage={defaultLanguage}
                    languageOptions={languageOptions}
                    onClose={handleClose}
                    handleSave={handleSave}
                    handleAction={handleAction}
                />
            )}
            {!action.comment_choices.length && (
                <Card.EmptyState
                    icon={<Icon name="list" />}
                    onClick={() => handleEdit()}
                    title={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_empty_state.title')}
                    labelButton={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_empty_state.cta')}
                    subtitle={t('ticket.moderation.statusesTransitions.transition.steps.actions.comments_empty_state.description')}
                />
            )}
        </>
    );
};

const CommentModal = ({ action, comment, defaultLanguage, languageOptions, handleAction, handleSave, onClose }) => {
    const { t } = useTranslation();

    const handleChange = React.useCallback(
        (field, value, selectedLanguage) => {
            const comments = [...action.comment_choices].filter(cmt => cmt.id !== comment.id);
            comment.translations[field][selectedLanguage] = value;

            if (selectedLanguage !== defaultLanguage) {
                if (!value) {
                    delete comment.translations[field][selectedLanguage];
                }
            } else {
                comment[field] = value;
            }
            handleAction('comment_choices', [...comments, comment]);
        },
        [handleAction, action.comment_choices, comment, defaultLanguage]
    );

    const handleSubmit = React.useCallback(() => {
        handleSave(false);
        onClose();
    }, [handleSave, onClose]);

    return (
        <Modal
            size="large"
            title={t(`ticket.moderation.statusesTransitions.transition.steps.actions.comments_modal.title_${comment.id ? 'edit' : 'create'}`)}
            onClose={onClose}
            submitButton={<Button primary disabled={!comment.label || !comment.comment} onClick={handleSubmit} label={t('ticket.common.cta.save')} />}
            cancelButton={onClose && <Button onClick={onClose} label={t('ticket.common.cta.back')} />}
        >
            <TranslatableInput
                field="label"
                item={comment}
                handleChange={handleChange}
                defaultLanguage={defaultLanguage}
                languageOptions={languageOptions}
                label_prefix="ticket.moderation.statusesTransitions.transition.steps.actions.comments_modal"
            />
            <TranslatableInput
                field="comment"
                item={comment}
                inputType="text"
                handleChange={handleChange}
                defaultLanguage={defaultLanguage}
                languageOptions={languageOptions}
                label_prefix="ticket.moderation.statusesTransitions.transition.steps.actions.comments_modal"
            />
        </Modal>
    );
};
